import { useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import {
  type CalendarItem,
  formatDayHeading,
  kindLabel,
  kindPill,
  KIND_SORT,
  statusPill,
} from './calendarShared';

function sortItems(items: CalendarItem[]): CalendarItem[] {
  return [...items].sort((a, b) => {
    const kindDiff = KIND_SORT[a.kind] - KIND_SORT[b.kind];
    if (kindDiff !== 0) return kindDiff;
    return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
  });
}

export default function CalendarDayDetailModal({
  dateKey,
  items,
  onSelectItem,
  onClose,
}: {
  dateKey: string | null;
  items: CalendarItem[];
  onSelectItem: (item: CalendarItem) => void;
  onClose: () => void;
}) {
  const sorted = useMemo(() => sortItems(items), [items]);

  const contentByStatus = useMemo(() => {
    const map = new Map<string, { label: string; count: number }>();
    for (const item of items) {
      if (item.kind !== 'content') continue;
      const raw = (item.status ?? '').trim();
      const key = raw.toLowerCase() || 'unknown';
      const label = raw || 'Unknown';
      const existing = map.get(key);
      if (existing) existing.count += 1;
      else map.set(key, { label, count: 1 });
    }
    return Array.from(map.entries())
      .map(([key, v]) => ({ key, label: v.label, count: v.count }))
      .sort((a, b) => b.count - a.count);
  }, [items]);

  useEffect(() => {
    if (!dateKey) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dateKey, onClose]);

  if (!dateKey) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl overflow-hidden max-h-[min(90vh,36rem)] flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="calendar-day-title"
      >
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-3 shrink-0">
          <div>
            <h2 id="calendar-day-title" className="text-base font-semibold text-gray-900 dark:text-white">
              {formatDayHeading(dateKey)}
            </h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {items.length} item{items.length === 1 ? '' : 's'} on this day
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {contentByStatus.length > 0 ? (
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700/80 bg-gray-50/80 dark:bg-gray-900/30 shrink-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
              Content by status
            </div>
            <div className="flex flex-wrap gap-2">
              {contentByStatus.map((s) => (
                <span
                  key={s.key}
                  className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${statusPill(s.label)}`}
                >
                  {s.label}
                  <span className="opacity-80">{s.count}</span>
                </span>
              ))}
            </div>
          </div>
        ) : null}

        <ul className="overflow-y-auto p-3 space-y-1.5">
          {sorted.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onSelectItem(item)}
                className="w-full text-left rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {item.title}
                  </span>
                  <span className={`shrink-0 inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${kindPill(item.kind)}`}>
                    {kindLabel(item.kind)}
                  </span>
                </div>
                {item.kind === 'content' && item.status ? (
                  <div className="mt-1">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${statusPill(item.status)}`}>
                      {item.status}
                    </span>
                  </div>
                ) : null}
                {item.subtitle ? (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{item.subtitle}</p>
                ) : null}
              </button>
            </li>
          ))}
        </ul>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
